import { useLocation, useNavigate } from 'react-router';
import { AuthTitle } from '../../ui/AuthTitle/AuthTitle';
import { AuthMessage } from '../../ui/styles';
import { Button } from '@/components/Button/Button';
import { Box } from '@/components/Box/Box';
import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sendForgotPassword } from './api';
import { useFormik } from 'formik';
import { checkEmailValidationSchema } from './const/const';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { AuthLayout } from '../../ui/AuthLayout/AuthLayout';
import { AuthStep } from '../../ui/AuthStep/AuthStep';
import { Notification } from '@/components/Notification/Notification';
import { ForgotPasswordPayload } from '@/api/v1/users/public/forgotPasswordApi';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { GoogleReCaptcha, GoogleReCaptchaType } from '@/components/GoogleReCaptcha/GoogleReCaptcha';
import { getForgotPasswordEmailSentPath } from '../ForgotPasswordEmailSentPage/path/path';
import { useAuthNavigateToLogin } from '@/bundle/Auth/hooks/useAuthNavigateToLogin';

export const ForgotPasswordCheckEmailPage = () => {
  const navigate = useNavigate();
  const recaptchaRef = useRef<GoogleReCaptchaType>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const location = useLocation();
  const { authNavigateToLogin } = useAuthNavigateToLogin();

  const email = location.state?.email ? location.state.email : '';

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['send_forgot_password'],
    mutationFn: (payload: ForgotPasswordPayload) => {
      return sendForgotPassword(payload);
    },
    onSuccess(response) {
      if (response?.error) {
        recaptchaRef.current?.resetCaptcha();

        return;
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.ForgotPasswordSendLinkToEmail);

      return navigate(getForgotPasswordEmailSentPath());
    },
  });

  const formik = useFormik({
    initialValues: {
      email,
    },
    onSubmit: ({ email }) => {
      setCaptchaLoading(true);

      recaptchaRef.current
        ?.executeCaptcha()
        .then((token) => {
          setCaptchaLoading(false);

          const payload = {
            email,
            recaptcha_response: token,
          };

          mutate(payload);
        })
        .catch((error) => {
          console.error('google captcha failed with', error?.message);

          setCaptchaLoading(false);
        });
    },
    enableReinitialize: true,
    validationSchema: checkEmailValidationSchema,
  });

  const captchaError = getResponseError(data?.error, 'recaptcha_response');

  return (
    <AuthLayout>
      <AuthStep>
        <AuthTitle>Set Your New Password</AuthTitle>
        <Box marginBottom='36px'>
          <AuthMessage>
            Please enter the email address associated with your account and we will send you a link to set your new
            password.
          </AuthMessage>
        </Box>

        {!!captchaError && (
          <Notification variant='error' marginBottom='24px'>
            {captchaError}
          </Notification>
        )}

        <FormikForm value={formik}>
          <FormikInput
            autoComplete='new-field'
            name='email'
            label='Email Address*'
            placeholder='Enter Your Email Address'
            apiError={data?.error}
          />

          <GoogleReCaptcha ref={recaptchaRef} />
          <Box marginTop='18px' marginBottom='16px'>
            <Button isLoading={isPending || captchaLoading} onClick={formik.handleSubmit} disabled={!formik.isValid}>
              Send Link
            </Button>
          </Box>
        </FormikForm>

        <Box justifyContent='center'>
          <Button variant='secondary' onClick={authNavigateToLogin}>
            Return to Login
          </Button>
        </Box>
      </AuthStep>
    </AuthLayout>
  );
};
